package org.gymly.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.ButtonDefaults
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.text.font.FontWeight
import org.gymly.model.ClientPaymentMethod
import org.gymly.model.PortalMembershipOptions
import org.gymly.model.PortalPlanOption
import org.gymly.model.PortalProofRules
import org.gymly.theme.GymlyColors

/** Métodos en dólares: el precio del plan que se cobra es el de $ (Zelle y USDT). */
fun ClientPaymentMethod.cobraEnDolares(): Boolean =
    this == ClientPaymentMethod.ZELLE || this == ClientPaymentMethod.USDT ||
        this == ClientPaymentMethod.CARD || this == ClientPaymentMethod.PAYPAL

/**
 * Métodos que se muestran al pagar: los que exigen comprobante y, para Zelle y USDT, solo si el plan elegido
 * tiene precio en $. Sin plan elegido, se muestran todos los que exigen comprobante.
 */
fun membershipMethodChoices(
    options: PortalMembershipOptions?,
    planId: String?,
): List<ClientPaymentMethod> {
    val plan = options?.plans?.firstOrNull { it.id == planId }
    return ClientPaymentMethod.entries
        // Tarjeta y PayPal: solo si el backend los marca como disponibles (habilitados y con pasarela lista).
        .filter { it.requiresProof() || (it.isGateway() && PortalProofRules.rules.containsKey(it.name)) }
        .filter { !it.cobraEnDolares() || plan == null || plan.priceUsdCents > 0L }
}

/** Monto que cobrará el servidor para el plan y el método: Bs o $ según el método. */
fun membershipAmountLabel(plan: PortalPlanOption?, method: ClientPaymentMethod): String {
    if (plan == null) return "Elige un plan para ver el monto."
    return if (method.cobraEnDolares()) {
        "Monto: \$" + centsLabel(plan.priceUsdCents)
    } else {
        "Monto: Bs " + centsLabel(plan.priceCents)
    }
}

private fun centsLabel(cents: Long): String {
    val whole = cents / 100
    val frac = (cents % 100).toString().padStart(2, '0')
    return "$whole,$frac"
}

/** Elección de plan y entrenador, con el monto que se cobrará. */
@Composable
fun MembershipPlanTrainerFields(
    options: PortalMembershipOptions?,
    planId: String?,
    onPlanId: (String) -> Unit,
    trainerId: String?,
    onTrainerId: (String) -> Unit,
    method: ClientPaymentMethod,
) {
    val plan = options?.plans?.firstOrNull { it.id == planId }
    Text("Plan", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
    Spacer(Modifier.height(6.dp))
    if (options == null) {
        Text("No se pudieron cargar los planes.", color = GymlyColors.TextMuted, fontSize = 12.sp)
    }
    options?.plans?.forEach { p ->
        ChoiceButton(text = p.name, selected = p.id == planId, onClick = { onPlanId(p.id) })
    }
    Spacer(Modifier.height(14.dp))
    Text("Entrenador", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
    Spacer(Modifier.height(6.dp))
    options?.trainers?.forEach { t ->
        ChoiceButton(text = t.name, selected = t.id == trainerId, onClick = { onTrainerId(t.id) })
    }
    Spacer(Modifier.height(14.dp))
    Text(
        membershipAmountLabel(plan, method),
        fontWeight = FontWeight.SemiBold,
        fontSize = 14.sp,
        color = GymlyColors.Text,
    )
}

@Composable
private fun ChoiceButton(text: String, selected: Boolean, onClick: () -> Unit) {
    OutlinedButton(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth().padding(bottom = 6.dp),
        colors = ButtonDefaults.outlinedButtonColors(
            containerColor = if (selected) GymlyColors.Primary.copy(alpha = 0.12f) else GymlyColors.Surface,
            contentColor = GymlyColors.Text,
        ),
    ) {
        Row(horizontalArrangement = Arrangement.Start) {
            Text(
                if (selected) "● $text" else text,
                fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
                fontSize = 14.sp,
            )
        }
    }
}

/**
 * Referencia del pago: en pago móvil solo se guardan los últimos 5 dígitos, igual que en el web admin.
 * Los demás métodos se dejan tal cual.
 */
fun referenceInputFor(methodName: String, raw: String): String =
    if (methodName == "MOBILE_PAYMENT") raw.filter { it.isDigit() }.takeLast(5) else raw

/** Pago móvil necesita exactamente los 5 dígitos de la referencia. */
fun isReferenceValid(methodName: String, reference: String): Boolean =
    methodName != "MOBILE_PAYMENT" || reference.length == 5
